import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Shield, Lock, Eye, CheckCircle } from 'lucide-react';

const PrivacyEU = () => {
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
    window.location.href = homeUrl;
  };

  return (
    <Layout>
      <SEOHead 
        title="GDPR-Compliant Salesforce AI Copilot, NoQ5.ai"
        description="EU-based Salesforce AI Copilot with GDPR-first design. Anonymisation before LLMs, least-privilege API access, and audit trails for write-backs."
        canonicalUrl="https://noq5.ai/privacy-eu"
      />
      <div className="container px-4 md:px-6 py-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <div className="inline-block rounded-lg bg-green-100 px-3 py-1 text-sm text-green-800 mb-4">
              🇪🇺 EU-Based & GDPR Compliant
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              GDPR-Compliant, EU-Based (Ireland)
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              NoQ5.ai is built in Europe and complies with GDPR. We implement a privacy-by-design approach where sensitive fields are anonymised before any LLM processing.
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">EU Data Residency</h3>
                <p className="text-gray-600">All data processing and storage happens within EU borders in Ireland. No transfers to the US or other regions.</p>
              </div>
              
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <Lock className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Privacy by Design</h3>
                <p className="text-gray-600">Sensitive data is anonymised before AI processing. Personal information never reaches language models.</p>
              </div>
              
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <Eye className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Audit Trails</h3>
                <p className="text-gray-600">Complete transparency on all CRM write-backs with full audit trails and role-based permissions.</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">Data Handling & Security</h2>
            
            <div className="bg-oli-blue-light/20 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                Our Privacy-First Approach
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>Least-privilege API access:</strong> NoQ5.ai only requests necessary Salesforce permissions and respects your organisation's sharing rules and field-level security.
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>Encrypted in transit:</strong> All communications between NoQ5.ai and Salesforce use industry-standard encryption protocols.
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-oli-blue rounded-full mt-2 mr-3"></div>
                  <div>
                    <strong>Strict access controls:</strong> Role-based access ensures users can only update records they have permission to modify.
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">Security Posture</h2>
            
            <p className="mb-6">
              NoQ5.ai maintains enterprise-grade security standards while remaining accessible for teams of all sizes:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <div className="border-l-4 border-oli-blue pl-4">
                  <h4 className="font-semibold">Role-based permissions</h4>
                  <p className="text-gray-600 text-sm">Alignment with your organisation's existing Salesforce user roles and permissions</p>
                </div>
                <div className="border-l-4 border-oli-blue pl-4">
                  <h4 className="font-semibold">Audit trails for write-backs</h4>
                  <p className="text-gray-600 text-sm">Complete visibility into all CRM updates with timestamps and user attribution</p>
                </div>
                <div className="border-l-4 border-oli-blue pl-4">
                  <h4 className="font-semibold">Data anonymisation</h4>
                  <p className="text-gray-600 text-sm">Personal information tokenised before AI processing, ensuring GDPR compliance</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="border-l-4 border-oli-blue pl-4">
                  <h4 className="font-semibold">EU infrastructure</h4>
                  <p className="text-gray-600 text-sm">Hosted exclusively in Ireland with no cross-border data transfers</p>
                </div>
                <div className="border-l-4 border-oli-blue pl-4">
                  <h4 className="font-semibold">Sharing rules compliance</h4>
                  <p className="text-gray-600 text-sm">Respects your Salesforce organisation's sharing model and field-level security</p>
                </div>
                <div className="border-l-4 border-oli-blue pl-4">
                  <h4 className="font-semibold">Regular security reviews</h4>
                  <p className="text-gray-600 text-sm">Ongoing security assessments and compliance monitoring</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">GDPR Compliance Features</h2>
            
            <div className="space-y-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-3">Data Subject Rights</h4>
                <p className="text-gray-600 mb-3">
                  We support all GDPR data subject rights including access, portability, rectification, erasure, and objection.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Data access requests processed within 30 days</li>
                  <li>• Right to rectification through standard Salesforce processes</li>
                  <li>• Data portability in machine-readable formats</li>
                  <li>• Right to erasure with secure deletion protocols</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-3">Legal Basis & Processing</h4>
                <p className="text-gray-600 mb-3">
                  NoQ5.ai processes personal data under legitimate interests for CRM functionality, with appropriate safeguards and balancing tests.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Clear legal basis documentation</li>
                  <li>• Data Processing Impact Assessments (DPIAs) available</li>
                  <li>• Purpose limitation and data minimisation principles</li>
                  <li>• Appropriate technical and organisational measures</li>
                </ul>
              </div>
            </div>

            <div className="bg-oli-blue text-white p-8 rounded-lg text-center mb-12">
              <h3 className="text-2xl font-bold mb-4">Try NoQ5.ai with Your Salesforce Sandbox</h3>
              <p className="mb-6">
                Experience GDPR-compliant Salesforce automation with complete data protection and EU-based infrastructure.
              </p>
              <Button 
                onClick={scrollToDemo}
                className="bg-white text-oli-blue hover:bg-gray-100"
              >
                Try NoQ5.ai Now
              </Button>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">Where exactly is NoQ5.ai hosted?</h3>
                <p className="text-gray-600">
                  NoQ5.ai's infrastructure is located in Ireland, which is part of the European Union. All data processing, storage, and AI inference happens within EU borders with no transfers to third countries.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">Do you provide Data Processing Agreements?</h3>
                <p className="text-gray-600">
                  Yes, we provide comprehensive Data Processing Agreements (DPAs) that meet GDPR Article 28 requirements, including processing purposes, data categories, retention periods, and security measures.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">How does anonymisation work without losing context?</h3>
                <p className="text-gray-600">
                  NoQ5.ai uses advanced tokenisation to replace sensitive identifiers with contextual tokens before AI processing. The system maintains semantic meaning while protecting personal data, then maps tokens back to actual values when writing to Salesforce.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-3">What happens to data when I stop using NoQ5.ai?</h3>
                <p className="text-gray-600">
                  Upon service termination, all personal data is securely deleted within 30 days. We provide data export options and follow secure deletion protocols with certification of destruction.
                </p>
              </div>
            </div>

            <div className="mt-12 p-6 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">Need More Information?</h3>
              <p className="text-gray-600 mb-4">
                Our privacy and legal teams are available to discuss specific GDPR requirements, provide additional documentation, or answer technical questions about our EU-based infrastructure.
              </p>
              <p className="text-sm text-gray-500">
                For privacy inquiries: <a href="mailto:privacy@noq5.ai" className="text-oli-blue hover:text-oli-blue-dark underline">privacy@noq5.ai</a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default PrivacyEU;